import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Play, X } from "lucide-react";

import HomePage from "@/pages/Home";
import ExecutiveDashboard from "@/pages/ExecutiveDashboard";
import Directory from "@/pages/Directory";
import Venue from "@/pages/Venue";
import SchedulePage from "@/pages/Schedule";
import SupplierSuccess from "@/pages/SupplierSuccess";
import AskAI from "@/pages/AskAI";

const STEPS = [
  { Comp: HomePage, label: "Home", props: {} },
  { Comp: ExecutiveDashboard, label: "Executive Dashboard", props: { presenterMode: true } },
  { Comp: Directory, label: "Supplier Directory", props: {} },
  { Comp: Venue, label: "Interactive Venue", props: {} },
  { Comp: SchedulePage, label: "Schedule", props: {} },
  { Comp: SupplierSuccess, label: "Supplier Success", props: { presenterMode: true } },
  { Comp: AskAI, label: "AI Concierge", props: {} },
];

export default function Presentation() {
  const [i, setI] = useState(0);
  const nav = useNavigate();

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") setI((x) => Math.min(STEPS.length - 1, x + 1));
      else if (e.key === "ArrowLeft") setI((x) => Math.max(0, x - 1));
      else if (e.key === "Escape") nav("/");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [nav]);

  const { Comp, label, props } = STEPS[i];

  return (
    <div className="relative">
      {/* Hide app bottom-nav while in presentation mode */}
      <style>{`@media (max-width: 767px) { body > div > footer + nav, nav.glass-nav.rounded-full { display: none !important; } }`}</style>
      {/* Slide */}
      <AnimatePresence mode="wait">
        <motion.div key={i}
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}>
          <Comp {...props} />
        </motion.div>
      </AnimatePresence>

      {/* Floating presenter dock */}
      <div className="fixed bottom-6 inset-x-0 z-50 flex justify-center px-4 pointer-events-none">
        <div className="pointer-events-auto bg-[#0A0A0A] text-white border border-white/10 rounded-full shadow-2xl flex items-center gap-2 pl-3 pr-2 py-2">
          <div className="px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-zinc-400 flex items-center gap-2">
            <Play size={10} className="text-[#A39171]" /> Presentation
          </div>
          <div className="flex items-center gap-2 border-l border-white/10 pl-3">
            <button onClick={() => setI((x) => Math.max(0, x - 1))} disabled={i === 0}
              className="h-9 w-9 rounded-full bg-white/5 hover:bg-white/15 disabled:opacity-30 flex items-center justify-center"
              data-testid="demo-prev"><ArrowLeft size={14} /></button>
            <div className="text-xs uppercase tracking-[0.18em] min-w-[180px] text-center">
              {i + 1} / {STEPS.length} · {label}
            </div>
            <button onClick={() => setI((x) => Math.min(STEPS.length - 1, x + 1))} disabled={i === STEPS.length - 1}
              className="h-9 w-9 rounded-full bg-white/5 hover:bg-white/15 disabled:opacity-30 flex items-center justify-center"
              data-testid="demo-next"><ArrowRight size={14} /></button>
          </div>
          <button onClick={() => nav("/")} className="ml-2 h-9 px-3 text-xs uppercase tracking-[0.18em] bg-white/5 hover:bg-white/15 rounded-full flex items-center gap-2" data-testid="demo-exit">
            <X size={12} /> Exit
          </button>
        </div>
      </div>

      {/* Step rail */}
      <div className="fixed top-1/2 -translate-y-1/2 right-3 z-40 hidden md:flex flex-col gap-2">
        {STEPS.map((_, idx) => (
          <button key={idx} onClick={() => setI(idx)}
            className={`w-1.5 transition-all ${idx === i ? "h-8 bg-[#A39171]" : "h-3 bg-zinc-400/40 hover:bg-zinc-400"} rounded-full`}
            aria-label={`Go to slide ${idx + 1}`} />
        ))}
      </div>
    </div>
  );
}
